import type { File as FileResource } from '@terafile/shared'
import { useEffect } from 'react'
import { useAuth } from '../lib/auth-context.js'
import { classifyFile } from '../lib/file-kind.js'
import { LOADING } from '../lib/labels.js'
import { usePreviewUrl } from '../lib/use-preview-url.js'
import { ModalOverlay } from './ModalOverlay.js'
import { Watermark } from './Watermark.js'

type Props = {
  file: FileResource | null
  onClose: () => void
}

/**
 * 画像 / PDF のフルプレビュー用モーダル。
 *
 * - 画像: 原本（R2 署名 URL）を `<img>` で表示。最大寸はビューポート内。
 * - PDF: 原本を `<iframe>` で表示（ブラウザ標準 PDF viewer に委ねる）。
 * - 動画は別途 VideoPlayer 経路を使うため、このモーダルでは扱わない。
 *
 * ESC キー or 背景クリックで閉じる。
 */
export function FileViewerModal({ file, onClose }: Props) {
  const { session } = useAuth()
  // メアド等の PII は焼かず、user.id 短縮で追跡可能性を維持しつつ
  // 先頭にサービス名を入れて流出時のブランド露出も兼ねる。
  const viewerTag = session?.user.id?.slice(0, 8) ?? 'viewer'
  const watermarkLabel = `テラファイル便 · ${viewerTag}`

  const kind = file ? classifyFile(file.mimeType) : 'other'
  const enabled = !!file && (kind === 'image' || kind === 'pdf')
  const { url, isLoading, error } = usePreviewUrl(file?.id ?? '', { enabled })

  // ESC で閉じる（ModalOverlay の onEscape と併用）
  useEffect(() => {
    if (!file) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [file, onClose])

  if (!file) return null

  return (
    <ModalOverlay
      className="bg-black/85"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label={`${file.originalName} のプレビュー`}
    >
      {/* ヘッダ */}
      <div className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center justify-between text-white pointer-events-none z-[51]">
        <div className="min-w-0 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            Preview
          </span>
          <span className="text-sm font-medium truncate">{file.originalName}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="pointer-events-auto text-white/80 hover:text-white text-sm px-3 py-1.5 border border-white/30 rounded-md hover:bg-white/10 transition-colors"
        >
          閉じる (Esc)
        </button>
      </div>

      {/* 本体: 背景クリック透過のため stopPropagation */}
      <div
        className="relative w-full max-w-6xl max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        {isLoading && <p className="text-white/70 text-sm">{LOADING.fetching}</p>}
        {error && (
          <p className="text-red-300 text-sm">プレビューの取得に失敗しました ({error.message})</p>
        )}
        {url && kind === 'image' && (
          <div className="relative">
            <img
              src={url}
              alt={file.originalName}
              className="max-w-full max-h-[88vh] object-contain shadow-2xl bg-black/40"
            />
            {/* ウォーターマーク: スクリーンキャプチャ追跡用（F-PREV-12） */}
            <Watermark identifier={watermarkLabel} hint={file.originalName} />
          </div>
        )}
        {url && kind === 'pdf' && (
          <div className="relative w-full">
            <iframe
              src={url}
              title={file.originalName}
              className="w-full h-[88vh] bg-white rounded-md shadow-2xl"
            />
            {/* PDF 上のウォーターマーク。iframe を覆うので操作の邪魔にならないよう
                 pointer-events: none は Watermark コンポーネント内で適用済み */}
            <Watermark identifier={watermarkLabel} hint={file.originalName} />
          </div>
        )}
        {!isLoading && !error && !['image', 'pdf'].includes(kind) && (
          <p className="text-white/70 text-sm">この種別はプレビューできません</p>
        )}
      </div>
    </ModalOverlay>
  )
}
